'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { connect } from 'react-redux';
import { createLocation } from 'history';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Map as ImmutableMap } from 'immutable';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';
import NotificationList from 'ReduxMessenger/components/NotificationList';
import { parse, stringify } from 'hub-http/helpers/params';
import UIDashboardPage from 'UIComponents/page/UIDashboardPage';
import SocialComposerPanel from 'social-composer-lib/components/SocialComposerPanel';
import UIUpgradeBanner from 'ui-addon-upgrades/ums/UIUpgradeBanner';
import { LayoutWithCollaborationSidebar, FEATURES, SUPPORTED_HUBS } from 'collaboration-sidebar';
import { SuspensionBannersContextProvider, ContextSuspensionBanners } from 'ui-suspension-banners-lib';
import FeedbackLoader from 'feedback-loader';
import OnboardingTour from 'onboarding-tours/components/OnboardingTour';
import { CONTENT_TYPE_PROPS, ContentTypes, PATHS, PATHS_PROPS, REQUEST_STATUS_PROPS, RequestStatus, ContentTypeToCommentObjectType, LANDING_PAGES_TRUSTED_SCOPE, ACCOUNT_VERIFICATION_SOURCE } from '../constants/base';
import ErrorPage from '../components/ErrorPage';
import { getContentAbsoluteUrl, getContentHasTranslatedContent, getContentRequestErrorStatusCode, getContentRequestErrorType, getContentIsVariant, getContentRequestStatus } from '../selectors/contentSelectors';
import { getHasMAB, getMABMasterId, getMABStatus } from '../selectors/mabSelectors';
import { getUserHasVideo, getUserHasGoogleSearchConsoleAccess, getUserHasReadAccess } from '../selectors/authSelectors';
import { getHeaderParams } from '../utils/headerParams';
import { getHistoryCreateHref, getHistoryPush } from '../selectors/historySelectors';
import { getLocationPathname, getLocationRoute, getMatchDefaultPath, getLocationSection } from '../selectors/locationSelectors';
import { getMatchContentPageId, getMatchContentSlug, getMatchContentSlugType, getMatchCrmObjectTypeId, getMatchHubDbTableId, getMatchPageId, getMatchIsInternalContent } from '../selectors/matchSelectors';
import { getVariationsHasVariations } from '../selectors/variationsSelectors';
import { makeAsyncComponent } from '../utils/makeAsyncUtils';
import { navigatedToExternalPage, navigatedToPage } from '../actions/ApplicationActions';
import OptimizationSidebar from '../components/OptimizationSidebar';
import { usageTracker } from '../utils/usageTracker';
import { PENDING, SUCCEEDED } from 'reporting-data/redux/StatusTypes';
import { getSocialComopserOpen, getSocialComposerParams, getAdsPanelOpen } from '../selectors/modalsDisplaySelectors';
import { closeSocialComposer } from '../actions/ModalActions';
import TriggerWootricSurvey from '../components/TriggerWootricSurvey';
import { getShouldShowFreeBlogRemoveBrandingBanner, getShouldShowFreeLandingPagesRemoveBrandingBanner, getShouldShowFreeSitePagesRemoveBrandingBanner } from '../selectors/contentUpgradeSelectors';
import ContentUsageLimitUpgradeBanner from '../components/ContentUsageLimitUpgradeBanner';
const AsyncHeader = makeAsyncComponent(() => import(
/* webpackChunkName: "header", webpackPreload: true */
'./Header'), {
  maxRetries: 2
});
const AsyncPerformance = makeAsyncComponent(() => import(
/* webpackChunkName: "performance", webpackPrefetch: true */
'./Performance'), {
  maxRetries: 2
});
const AsyncOptimization = makeAsyncComponent(() => import(
/* webpackChunkName: "optimization" */
'./Optimization'), {
  maxRetries: 2
});
const AsyncSmartContentReport = makeAsyncComponent(() => import(
/* webpackChunkName: "smart-content" */
'./SmartContent'), {
  maxRetries: 2
});
const AsyncTestResultsLoader = makeAsyncComponent(() => import(
/* webpackChunkName: "test-results" */
'./TestResultsLoader'), {
  maxRetries: 2
});
const AsyncAdsPanel = makeAsyncComponent(() => import(
/* webpackChunkName: "ads-panel" */
'./AdsPanel'), {
  maxRetries: 2
});

const mapStateToProps = (state, props) => Object.assign({}, getHeaderParams(props), {
  absoluteUrl: getContentAbsoluteUrl(state, props),
  adsPanelOpen: getAdsPanelOpen(state),
  contentPageId: getMatchContentPageId(state, props),
  contentRequestErrorStatusCode: getContentRequestErrorStatusCode(state),
  contentRequestErrorType: getContentRequestErrorType(state),
  contentRequestStatus: getContentRequestStatus(state),
  contentSlug: getMatchContentSlug(state, props),
  contentType: getMatchContentSlugType(state, props),
  createHref: getHistoryCreateHref(state, props),
  crmObjectTypeId: getMatchCrmObjectTypeId(state, props),
  currentSection: getLocationSection(state, props),
  defaultPath: getMatchDefaultPath(state, props),
  hasContentAccess: getUserHasReadAccess(state, props),
  hasMab: getHasMAB(state),
  hasTranslatedContent: getContentHasTranslatedContent(state),
  hasVariations: getVariationsHasVariations(state),
  hasVideo: getUserHasVideo(state),
  hubDbTableId: getMatchHubDbTableId(state, props),
  isInternal: getMatchIsInternalContent(state, props),
  isVariant: getContentIsVariant(state),
  mabStatus: getMABStatus(state),
  masterMabId: getMABMasterId(state),
  pageId: getMatchPageId(state, props),
  pathname: getLocationPathname(state, props),
  push: getHistoryPush(state, props),
  route: getLocationRoute(state, props),
  shouldShowFreeBlogRemoveBrandingBanner: getShouldShowFreeBlogRemoveBrandingBanner(state, props),
  shouldShowFreeLandingPagesRemoveBrandingBanner: getShouldShowFreeLandingPagesRemoveBrandingBanner(state, props),
  shouldShowFreeSitePagesRemoveBrandingBanner: getShouldShowFreeSitePagesRemoveBrandingBanner(state, props),
  socialComposerOpen: getSocialComopserOpen(state),
  socialComposerParams: getSocialComposerParams(state),
  userHasGoogleSearchConsoleAccess: getUserHasGoogleSearchConsoleAccess(state)
});

const mapDispatchToProps = {
  closeSocialComposer,
  navigatedToExternalPage,
  navigatedToPage
};

class Application extends Component {
  constructor(...args) {
    super(...args);

    this.getDefaultTrackingParams = () => {
      const {
        contentType,
        currentSection
      } = this.props;
      return {
        'page-type': contentType,
        screen: 'detail',
        subscreen: currentSection
      };
    };

    this.getPage = () => {
      const {
        contentPageId,
        contentType,
        crmObjectTypeId,
        hasVideo,
        hubDbTableId,
        pageId,
        location
      } = this.props;

      if (contentType === ContentTypes.EXTERNAL_PAGE) {
        const params = parse(location.search.slice(1));
        this.props.navigatedToExternalPage(Object.assign({
          id: pageId
        }, params));
      } else {
        this.props.navigatedToPage({
          contentPageId,
          contentType,
          crmObjectTypeId,
          hasVideo,
          hubDbTableId
        });
      }
    };

    this.handleSidebarCreateHref = sidebar => {
      const {
        contentSlug,
        createHref,
        location,
        pageId
      } = this.props;
      const pathname = contentSlug === ContentTypes.EXTERNAL_PAGE ? `/${contentSlug}/optimization/${sidebar}` : `/${contentSlug}/${pageId}/optimization/${sidebar}`;
      return createHref(createLocation({
        pathname,
        search: location.search
      }, null, null, location));
    };

    this.handleTabCreateHref = tab => {
      const {
        contentSlug,
        createHref,
        location,
        pageId
      } = this.props;
      return createHref(createLocation({
        pathname: `/${contentSlug}/${pageId}/${tab}`,
        search: location.search
      }, null, null, location));
    };

    this.handleTabOnClick = tab => {
      const {
        contentSlug,
        location,
        pageId,
        push
      } = this.props;
      usageTracker.track('exploreReport', Object.assign({}, this.getDefaultTrackingParams(), {
        action: 'clicked-tab',
        tab
      }));
      push({
        pathname: `/${contentSlug}/${pageId}/${tab}`,
        search: location.search
      });
    };

    this.handlePageCreateHref = (pageId, tab = this.props.currentSection) => {
      const {
        contentSlug,
        createHref,
        location
      } = this.props;
      return createHref(createLocation({
        pathname: `/${contentSlug}/${pageId}/${tab}`,
        search: location.search
      }, null, null, location));
    };

    this.handlePageChange = (pageId, tab = this.props.currentSection, search = '') => {
      const {
        contentSlug,
        push
      } = this.props;
      push({
        pathname: `/${contentSlug}/${pageId}/${tab}`,
        search
      });
    };

    this.redirectToMasterVariant = () => {
      const {
        currentSection,
        hasMab,
        isVariant,
        masterMabId
      } = this.props;

      if (!(isVariant && hasMab && masterMabId)) {
        return;
      }

      const search = this.getCurrentPageVariantSearch();
      this.handlePageChange(masterMabId, currentSection, search);
    };
  }

  componentDidMount() {
    setDocumentTitle('app.htmlTitle');

    if (this.props.hasContentAccess) {
      AsyncHeader.preload();
      this.getPage();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      mabStatus,
      pageId
    } = this.props;

    if (prevProps.pageId !== pageId) {
      this.getPage();
    }

    if (prevProps.mabStatus === PENDING && mabStatus === SUCCEEDED) {
      this.redirectToMasterVariant(prevProps);
    }
  }

  getCurrentPageVariantSearch() {
    const {
      contentPageId,
      location
    } = this.props;
    const currentQuery = parse(location.search ? location.search.replace('?', '') : '');
    return stringify(Object.assign({}, currentQuery, {
      variation: contentPageId
    }));
  }

  renderHeader() {
    return /*#__PURE__*/_jsx(AsyncHeader, {
      createTabHref: this.handleTabCreateHref,
      onPageChange: this.handlePageChange,
      onPageCreateHref: this.handlePageCreateHref,
      onTabClick: this.handleTabOnClick
    });
  }

  renderSidebar() {
    const {
      currentSection,
      pathname,
      push,
      userHasGoogleSearchConsoleAccess,
      isInternal
    } = this.props;

    if (currentSection !== PATHS.OPTIMIZATION) {
      return null;
    }

    return /*#__PURE__*/_jsx(OptimizationSidebar, {
      createHref: this.handleSidebarCreateHref,
      isInternal: isInternal,
      onClick: push,
      pathname: pathname,
      userHasGoogleSearchConsoleAccess: userHasGoogleSearchConsoleAccess
    });
  }

  renderSocialComposer() {
    return /*#__PURE__*/_jsx(SocialComposerPanel, Object.assign({
      clientTag: "content-detail-ui'",
      onCloseModal: this.props.closeSocialComposer,
      open: this.props.socialComposerOpen
    }, this.props.socialComposerParams.toJS()));
  }

  renderAdsPanel() {
    if (!this.props.adsPanelOpen) {
      return null;
    }

    return /*#__PURE__*/_jsx(AsyncAdsPanel, {});
  }

  renderPage() {
    const {
      contentSlug,
      defaultPath,
      pageId
    } = this.props;
    return /*#__PURE__*/_jsxs(UIDashboardPage, {
      headerComponent: this.renderHeader(),
      sidebarComponent: this.renderSidebar(),
      children: [/*#__PURE__*/_jsxs(Switch, {
        children: [/*#__PURE__*/_jsx(Route, {
          component: AsyncPerformance,
          path: `/:contentSlug/:pageId/${PATHS.PERFORMANCE}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncPerformance,
          path: `/:contentSlug/:pageId/${PATHS.PUBLISHED}`
        }), /*#__PURE__*/_jsx(Redirect, {
          from: `/:contentSlug/:pageId/${PATHS.SCHEDULED}`,
          to: `/:contentSlug/:pageId/${PATHS.PUBLISHED}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncOptimization,
          path: `/:contentSlug/:pageId?/${PATHS.OPTIMIZATION}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncSmartContentReport,
          path: `/:contentSlug/:pageId/${PATHS.SMART_CONTENT}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncTestResultsLoader,
          path: `/:contentSlug/:pageId/${PATHS.TEST_RESULTS}`
        }), /*#__PURE__*/_jsx(Redirect, {
          to: `/${contentSlug}/${pageId}/${defaultPath}`
        })]
      }), /*#__PURE__*/_jsx(NotificationList, {}), this.renderAdsPanel(), this.renderSocialComposer(), /*#__PURE__*/_jsx(FeedbackLoader, {})]
    });
  }

  render() {
    const {
      absoluteUrl,
      contentRequestErrorStatusCode,
      contentRequestErrorType,
      contentRequestStatus,
      contentType,
      hasContentAccess,
      shouldShowFreeBlogRemoveBrandingBanner,
      shouldShowFreeLandingPagesRemoveBrandingBanner,
      shouldShowFreeSitePagesRemoveBrandingBanner,
      contentPageId
    } = this.props;

    if (!hasContentAccess || contentRequestStatus === RequestStatus.FAILED && !absoluteUrl) {
      const statusCode = !hasContentAccess ? 403 : contentRequestErrorStatusCode;
      return /*#__PURE__*/_jsx(ErrorPage, {
        contentType: contentType,
        errorType: contentRequestErrorType,
        statusCode: statusCode
      });
    }

    const isWebsitePage = contentType === ContentTypes.SITE_PAGE;
    const appHub = isWebsitePage ? SUPPORTED_HUBS.CMS_HUB : [SUPPORTED_HUBS.CMS_HUB, SUPPORTED_HUBS.MARKETING_HUB];
    return /*#__PURE__*/_jsx(LayoutWithCollaborationSidebar, {
      appHub: appHub,
      appName: "content-detail",
      enabledFeatures: [FEATURES.COMMENTS, FEATURES.TASKS, FEATURES.CALENDAR],
      objectId: contentPageId,
      objectType: ContentTypeToCommentObjectType[contentType],
      scrollablePage: true,
      children: /*#__PURE__*/_jsxs(SuspensionBannersContextProvider, {
        children: [/*#__PURE__*/_jsx(ContextSuspensionBanners, {
          scope: LANDING_PAGES_TRUSTED_SCOPE,
          source: ACCOUNT_VERIFICATION_SOURCE
        }), /*#__PURE__*/_jsx(OnboardingTour, {}), shouldShowFreeLandingPagesRemoveBrandingBanner && /*#__PURE__*/_jsx(UIUpgradeBanner, {
          className: "m-x-5 m-top-5",
          upgradeKey: "landing-pages-details-page-remove-branding"
        }), shouldShowFreeSitePagesRemoveBrandingBanner && /*#__PURE__*/_jsx(UIUpgradeBanner, {
          className: "m-x-5 m-top-5",
          upgradeKey: "site-pages-details-page-remove-branding"
        }), shouldShowFreeBlogRemoveBrandingBanner && /*#__PURE__*/_jsx(UIUpgradeBanner, {
          className: "m-x-5 m-top-5",
          upgradeKey: "blog-posts-details-page-remove-branding"
        }), /*#__PURE__*/_jsx(ContentUsageLimitUpgradeBanner, {
          contentType: contentType
        }), this.renderPage(), /*#__PURE__*/_jsx(TriggerWootricSurvey, {
          enabled: true
        })]
      })
    });
  }

}

Application.propTypes = {
  absoluteUrl: PropTypes.string,
  accountStatus: PropTypes.string.isRequired,
  adsPanelOpen: PropTypes.bool.isRequired,
  closeSocialComposer: PropTypes.func.isRequired,
  contentPageId: PropTypes.string,
  contentRequestErrorStatusCode: PropTypes.number,
  contentRequestErrorType: PropTypes.string,
  contentRequestStatus: PropTypes.oneOf(REQUEST_STATUS_PROPS),
  contentSlug: PropTypes.string.isRequired,
  contentType: PropTypes.oneOf(CONTENT_TYPE_PROPS).isRequired,
  createHref: PropTypes.func.isRequired,
  crmObjectTypeId: PropTypes.string,
  currentSection: PropTypes.oneOf(PATHS_PROPS).isRequired,
  defaultPath: PropTypes.oneOf(PATHS_PROPS).isRequired,
  hasContentAccess: PropTypes.bool.isRequired,
  hasMab: PropTypes.bool.isRequired,
  hasTranslatedContent: PropTypes.bool.isRequired,
  hasVariations: PropTypes.bool.isRequired,
  hasVideo: PropTypes.bool.isRequired,
  hubDbTableId: PropTypes.string,
  isInternal: PropTypes.bool,
  isVariant: PropTypes.bool,
  languageVariation: PropTypes.string,
  location: PropTypes.object.isRequired,
  mabStatus: PropTypes.oneOf(REQUEST_STATUS_PROPS),
  masterMabId: PropTypes.string,
  navigatedToExternalPage: PropTypes.func.isRequired,
  navigatedToPage: PropTypes.func.isRequired,
  pageId: PropTypes.string,
  pathname: PropTypes.string.isRequired,
  push: PropTypes.func.isRequired,
  route: PropTypes.shape({
    path: PropTypes.string
  }),
  shouldShowFreeBlogRemoveBrandingBanner: PropTypes.bool.isRequired,
  shouldShowFreeLandingPagesRemoveBrandingBanner: PropTypes.bool.isRequired,
  shouldShowFreeSitePagesRemoveBrandingBanner: PropTypes.bool.isRequired,
  socialComposerOpen: PropTypes.bool.isRequired,
  socialComposerParams: PropTypes.instanceOf(ImmutableMap),
  userHasGoogleSearchConsoleAccess: PropTypes.bool.isRequired
};
const connectedComponent = connect(mapStateToProps, mapDispatchToProps)(Application);
export default connectedComponent;